import { useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { submitAccessRequest, type CaregiverRole } from '@/lib/crm';
import './AccessRequestForm.css';

/**
 * Access-request form → AXERP CRM.
 *
 * This is native markup, not the Frappe iframe: erp.tspgusa.com sends
 * `X-Frame-Options: SAMEORIGIN`, so an embed renders blank. See `lib/crm.ts`
 * for the full field contract and the two CRM constraints that shape it.
 */

const ROLES: CaregiverRole[] = [
  'Caring for a parent',
  'Caring for a spouse or partner',
  'Caring for a child or dependent',
  'Caring for more than one person',
  'Planning ahead / not yet caregiving',
  'Clinician, employer or partner',
];

const PLATFORMS = ['iOS', 'Android', 'Either'];

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function AccessRequestForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const uid = useId();
  const f = (name: string) => `${uid}-${name}`;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    // Let the browser's own validation UI handle field-level errors.
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const data = new FormData(form);

    // Honeypot: a real user never fills a hidden field. Report success and
    // drop it, so a bot gets no signal that it was detected.
    if (String(data.get('_gotcha') ?? '')) {
      form.reset();
      setStatus('sent');
      return;
    }

    setStatus('sending');
    setError('');

    const result = await submitAccessRequest({
      firstName: String(data.get('firstName') ?? ''),
      lastName: String(data.get('lastName') ?? ''),
      email: String(data.get('email') ?? ''),
      phone: String(data.get('phone') ?? ''),
      role: String(data.get('role') ?? '') as CaregiverRole,
      platform: String(data.get('platform') ?? ''),
      message: String(data.get('message') ?? ''),
    });

    if (result.ok) {
      form.reset();
      setStatus('sent');
    } else {
      setError(result.error);
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      // aria-live so a screen reader announces the swap — the form it replaced
      // is gone from the DOM, so nothing else would signal the change.
      <div className="arf arf--done surface-light" role="status" aria-live="polite">
        <div className="arf__check" aria-hidden="true">
          ✓
        </div>
        <h3>Request received</h3>
        <p>
          Thank you — your request is with our team. We review each one and normally reply within one
          business day with your onboarding link.
        </p>
        <p className="arf__done-note">
          Keep an eye on your inbox, and check your spam folder if you don’t see us.
        </p>
        <Button
          variant="ghost"
          onClick={() => {
            setStatus('idle');
            formRef.current?.reset();
          }}
        >
          Send another request
        </Button>
      </div>
    );
  }

  const sending = status === 'sending';

  // `surface-light`: pale card inside a dark section — see AccessRequestForm.css
  return (
    <form ref={formRef} className="arf surface-light" onSubmit={handleSubmit} noValidate>
      {status === 'error' && (
        <div className="arf__alert" role="alert">
          {error}
        </div>
      )}

      <div className="arf__row">
        <div className="arf__field">
          <label htmlFor={f('firstName')}>
            First name <span className="arf__req" aria-hidden="true">*</span>
          </label>
          <input id={f('firstName')} name="firstName" type="text" autoComplete="given-name" required />
        </div>
        <div className="arf__field">
          <label htmlFor={f('lastName')}>Last name</label>
          <input id={f('lastName')} name="lastName" type="text" autoComplete="family-name" />
        </div>
      </div>

      <div className="arf__row">
        <div className="arf__field">
          <label htmlFor={f('email')}>
            Email <span className="arf__req" aria-hidden="true">*</span>
          </label>
          <input id={f('email')} name="email" type="email" autoComplete="email" required />
        </div>
        <div className="arf__field">
          {/* Phone is required by the CRM web form itself, so the UI must
              require it too — otherwise Frappe rejects an otherwise valid
              submission and the user sees a confusing server error. */}
          <label htmlFor={f('phone')}>
            Mobile <span className="arf__req" aria-hidden="true">*</span>
          </label>
          <input id={f('phone')} name="phone" type="tel" autoComplete="tel" required />
        </div>
      </div>

      <div className="arf__field">
        <label htmlFor={f('role')}>
          Who are you caring for? <span className="arf__req" aria-hidden="true">*</span>
        </label>
        <select id={f('role')} name="role" required defaultValue="">
          <option value="" disabled>
            Select one…
          </option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="arf__field arf__fieldset">
        <legend>Which device?</legend>
        <div className="arf__radios">
          {PLATFORMS.map((p, i) => (
            <label key={p} className="arf__radio">
              <input type="radio" name="platform" value={p} defaultChecked={i === PLATFORMS.length - 1} />
              <span>{p}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="arf__field">
        <label htmlFor={f('message')}>Anything we should know?</label>
        <textarea
          id={f('message')}
          name="message"
          rows={4}
          placeholder="Optional — the situation you’re managing, or a question you want answered before you start."
        />
      </div>

      {/* Honeypot. Off-screen rather than display:none, and tabIndex -1 so
          keyboard users never land on it. */}
      <div className="arf__gotcha" aria-hidden="true">
        <label htmlFor={f('gotcha')}>Leave this field empty</label>
        <input id={f('gotcha')} name="_gotcha" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <Button type="submit" variant="honey" size="lg" block disabled={sending}>
        {sending ? 'Sending…' : 'Request access'}
      </Button>

      <p className="arf__legal">
        We use your details only to review this request and get back to you. No marketing lists, no
        third-party sharing.
      </p>
    </form>
  );
}
